import { useCallback } from 'react'

import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { zeroPad } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'

import { deleteCustomMeasurements, deleteViewerMeasurement } from '../store'

const useElementsPanel = (): ElementsPanelConfig => {
  // Store
  const dispatch = useAppDispatch()
  const userType = useAppSelector((state) => state.user.userType)

  const permissionSet = useAppSelector((state) => state.editor.permissionSet)
  const workingElementId = useAppSelector((state) => state.toolUtil.core.workingElementId)
  const savingElementIds = useAppSelector((state) => state.toolUtil.core.savingElementIds)

  const customMeasurements = useAppSelector((state) => state.toolCustomMeasurement.measurements)
  const tempMeasurements = useAppSelector((state) => state.toolCustomMeasurement.viewerMeasurements)

  // Flags
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  // Check if an element is being worked on
  const isWorking = useCallback((id: string) => workingElementId === id, [workingElementId])
  const isSaving = useCallback((id: string) => savingElementIds.includes(id), [savingElementIds])

  return {
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item']): EditorPanelItem[] => {
        // Custom measurement is parent-level, ignore if parent is provided
        if (parent) return []

        if (customMeasurements.length || tempMeasurements.length) {
          return [
            {
              key: 'panel-custom-measurements',
              name: '距離',
              isVirtualContainer: true,
              isCountable: true,
              itemType: InspectionItemType.LengthWithDistanceTool,
              children: customMeasurements
                .map<EditorPanelItem>((measurement, index) => {
                  const {
                    inspection_item_id,
                    part_name,
                    distanceLabel: { id },
                    order_in_inspection_group,
                    inspection_group_id,
                  } = measurement

                  return {
                    key: `panel-custom-measurement-${id}`,
                    name: part_name || `距離${zeroPad(index + 1, 3)}`,
                    item: { ...measurement, inspection_item_id: id } as InspectionItem,
                    order: order_in_inspection_group,
                    isGrouped: !!inspection_group_id,
                    isOrderable: true,
                    isWorking: isAllowedToModify && (isWorking(id) || !inspection_item_id),
                    isSaving: isAllowedToModify && isSaving(id),
                    onDelete: !inspection_item_id
                      ? () => {
                          dispatch(deleteCustomMeasurements([id]))
                          document.dispatchEvent(
                            new CustomEvent<{ id: string }>('editor:tool:customMeasurement:delete', {
                              detail: { id: measurement.distanceLabel.id },
                            }),
                          )
                        }
                      : isAllowedToModify && !isSaving(id),
                  }
                })
                .concat(
                  tempMeasurements.map((measurement, index) => ({
                    key: `panel-custom-measurement-${measurement.distanceLabel.id}`,
                    name: `距離${zeroPad(customMeasurements.length + index + 1, 3)}`,
                    item: measurement as InspectionItem,
                    onDelete: () => {
                      dispatch(deleteViewerMeasurement(measurement.distanceLabel.id))
                      document.dispatchEvent(
                        new CustomEvent<{ id: string }>('editor:tool:customMeasurement:delete', {
                          detail: { id: measurement.distanceLabel.id },
                        }),
                      )
                    },
                  })),
                ),
            },
          ]
        }

        return []
      },
      [customMeasurements, tempMeasurements, isAllowedToModify, isWorking, isSaving, dispatch],
    ),
  }
}

export default useElementsPanel
